import * as _ from "lodash";
import * as THREE from "three";
import {Node} from "../Node";
import {assertInstanceOf, assertNotNull, json, MetadataRow, PropertyParameters} from "../../global";
import {CellValidState} from "oc-app/metadata/validation/CellValidState";

export abstract class AbstractProperty<DataType, N extends Node> {

    node: N;
    name: string = null;
    label: string = undefined;
    nameThree: string = null;
    type: string = undefined;
    default: DataType = null;
    inputComponent = undefined;
    editable: boolean;
    metadataPersistent: boolean;
    defaultInJson = true;
    showInEditor: (AbstractProperty) => boolean = (p) => true;
    isThree: (AbstractProperty) => boolean = (p) => true;
    validate: (AbstractProperty) => CellValidState = (p) => undefined;

    constructor(args: PropertyParameters) {
        Object.assign(this, args);
        assertNotNull('name', this.name);
        assertNotNull('type', this.type);
        assertNotNull('node', this.node);
    }

    abstract typeCheck(value: DataType): boolean;

    isEqual(a: DataType, b: DataType): boolean {
        return a==b;
    }

    get(): DataType {
        const value = this.node[this.name] ?? this.createDefault();
        this._performTypeCheck(value);
        return value;
    }

    set(value: DataType) {
        const valueIntern = value ?? this.createDefault();
        this._performTypeCheck(valueIntern);
        this.node[this.name] = valueIntern;
        return valueIntern;
    }

    getNameThree(): string {
        return this.name;
    }

    _performTypeCheck(value: DataType) {
        if (value != null && !this.typeCheck(value)) {
            console.error('wrong data type=', value, 'for property=', this);
            throw new Error(this.name + ' has wrong type');
        }
    }

    createDefault(): DataType {
        return _.clone(this.default);
    }

    toJSON(): json {
        return this.get() as any;
    }

    fromJSON(json: json): this {
        this.set(json[this.name]);
        return this;
    }

    fromMetadataRow(metadataRow: MetadataRow): this {
        this.set(metadataRow[this.name]);
        return this;
    }

    toMetadataRow(metadataRow: MetadataRow): this {
        metadataRow[this.name] = this.get();
        return this;
    }

    toThree<ThreeInstanceType>(instance: ThreeInstanceType) {
        if (this.getNameThree() in instance) {
            instance[this.getNameThree()] = this.get();
        }
    }

    fromThree<ThreeInstanceType>(instance: ThreeInstanceType) {
        if (this.getNameThree() in instance) {
            this.set(this.threeToInternalFormat(instance[this.getNameThree()]));
        }
    }

    threeToInternalFormat(value) {
        return value;
    }

}

export function _toThreeComplex<DataType, ThreeInstanceType, ThreeValueType, N extends Node>(
    self: AbstractProperty<DataType, N>,
    instance: ThreeInstanceType,
    threeClassName: string,
    createThreeValuePromise: (instance: ThreeInstanceType, value: DataType) => { promise: Promise<ThreeValueType>, threeValue: ThreeValueType },
    setThreeCallback: (instance: ThreeInstanceType, value: DataType, threeValue: ThreeValueType, freshlyCreated: boolean) => void,
    alwaysRecreate = false
) {
    const value = self.get();
    const threeName = self.getNameThree();
    const threeValue = instance[threeName] as ThreeValueType;
    if (value == null && threeValue != null) {
        instance[threeName] = null;
    } else if (value != null && (alwaysRecreate || threeValue == null)) {
        const {promise, threeValue} = createThreeValuePromise(instance, value);
        if (threeValue) {
            instance[threeName] = threeValue;
        }
        if (promise) {
            promise.then((threeValue) => {
                instance[threeName] = threeValue;
                setThreeCallback(instance, value, instance[threeName], true);
            });
        }
    } else if (value != null && threeValue != null) {
        assertInstanceOf(self.node?.getName() + '.' + self.name + '.threeValue', threeValue, THREE[threeClassName]);
        setThreeCallback(instance, value, instance[threeName], false);
    }
}

export const _toThreeValuePromise = (threeValue) => ({promise: null, threeValue});
